/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { Form } from '../../molecules/PatternForm';
import StatusToast from 'components/molecules/StatusToast/StatusToast';
import { getAllRules, getAllChutes, updateTemplate } from '../../../services/templateService';
import { GetTemplateDetailUseCase } from '../../../templates/usecases/GetTemplateDetailUseCase';
import { AxiosTemplateRepository } from '../../../templates/infrastructure/AxiosTemplateRepository';
import { Template } from '../../../templates/domain/entities/Template';
import { Chute } from 'interfaces/templates';
import { useNavigate } from 'react-router-dom';
import { LoaderTransparent } from 'components/molecules/LoaderTransparent/LoaderTransparent';

interface Props {
  patternId: number;
}

interface Group {
  /** Unique stable key for DnD (separate from group_id which may be undefined for new rows) */
  dndId: string;
  group_id?: number;
  chutes: number[];
  maxCapacity?: number | null;
  provider?: { name: string; code: string } | null;
  providerService?: { name: string; code: string } | null;
}

const templateRepository = new AxiosTemplateRepository();
const getTemplateDetailUseCase = new GetTemplateDetailUseCase(templateRepository);

export default function PatternEdit({ patternId }: Props) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [ruleOptions, setRuleOptions] = useState<{ value: string; label: string }[]>([]);
  const [baseChutes, setBaseChutes] = useState<Chute[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [patternName, setPatternName] = useState('');
  const [toast, setToast] = useState<{
    title: string;
    type: 'success' | 'error' | 'loading';
    visible: boolean;
  }>({ title: '', type: 'success', visible: false });
  const [patternData, setPatternData] = useState<Template | null>(null);

  // ── DnD sensors ──────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    setGroups(prev => arrayMove(prev, fromIndex, toIndex));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setGroups(prev => {
        const oldIndex = prev.findIndex(g => g.dndId === active.id);
        const newIndex = prev.findIndex(g => g.dndId === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = useCallback((title: string, type: 'success' | 'error' | 'loading') => {
    setToast({ title, type, visible: true });
    setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);
  }, []);

  const handleAddRule = useCallback(() => {
    setGroups(prev => [
      ...prev,
      { dndId: `new-${Date.now()}`, chutes: [] },
    ]);
  }, []);

  const handleChutesSelected = useCallback((ruleIndex: number, selected: number[]) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = { ...updated[ruleIndex], chutes: selected };
      return updated;
    });
  }, []);

  const handleDeleteRule = useCallback((dndId: string) => {
    setGroups(prev => prev.filter(g => g.dndId !== dndId));
  }, []);

  const handleUpdateGroup = useCallback(
    (ruleIndex: number, group_id: number, chutes: number[]) => {
      setGroups(prev => {
        const updated = [...prev];
        updated[ruleIndex] = { ...updated[ruleIndex], group_id, chutes };
        return updated;
      });
    },
    [],
  );

  const handleMaxCapacityChange = useCallback((ruleIndex: number, value: number | null) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = { ...updated[ruleIndex], maxCapacity: value };
      return updated;
    });
  }, []);

  const handleUpdateRouteChange = useCallback(
    (
      ruleIndex: number,
      routeChangeConfig: {
        provider: { name: string; code: string } | null;
        providerService: { name: string; code: string } | null;
      },
    ) => {
      setGroups(prev => {
        const updated = [...prev];
        updated[ruleIndex] = { ...updated[ruleIndex], ...routeChangeConfig };
        return updated;
      });
    },
    [],
  );

  const showChutesSection = useMemo(
    () => patternData?.operationMode?.code === 'sorter',
    [patternData?.operationMode?.code],
  );

  const isButtonDisabled = useMemo(() => {
    if (!patternName.trim() || groups.length === 0) return true;
    const hasRuleWithoutGroup = groups.some(rule => typeof rule.group_id !== 'number');
    const hasInvalidProvider = groups.some(
      rule => rule.provider !== null && rule.providerService === null,
    );
    return hasRuleWithoutGroup || hasInvalidProvider;
  }, [patternName, groups]);

  const transformGroupsToPayload = useCallback(() => {
    return groups
      .filter((g): g is Group & { group_id: number } => typeof g.group_id === 'number')
      .map((group, index) => ({
        group_id: group.group_id,
        priority: index + 1,
        chutes: group.chutes
          .map(id => baseChutes.find(c => c.id === id)?.chute)
          .filter((chute): chute is number => chute !== undefined),
        max_capacity: group.maxCapacity && group.maxCapacity > 0 ? group.maxCapacity : null,
        provider: group.provider,
        provider_service: group.providerService,
      }));
  }, [groups, baseChutes]);

  const handleEdit = useCallback(async () => {
    if (!patternName.trim()) return showToast('El nombre de la plantilla es requerido', 'error');
    if (groups.length === 0) return showToast('Debe tener al menos una regla', 'error');

    try {
      await updateTemplate(patternId, {
        name: patternName.trim(),
        classification_type_id: patternData?.operationMode?.id ?? 0,
        groups: transformGroupsToPayload(),
      });
      showToast('Plantilla actualizada con éxito', 'success');
      navigate(`/templates/show/${patternId}`);
    } catch (e: any) {
      showToast(e.message ?? 'Error al actualizar la plantilla', 'error');
    }
  }, [patternName, groups, patternData, patternId, transformGroupsToPayload, showToast, navigate]);

  // ── Rule items (memoized) ─────────────────────────────────────────────────
  const ruleItems = useMemo(
    () =>
      groups.map((group, index) => {
        const defaultChutes = group.chutes
          .map(chute => {
            const match = baseChutes.find(c => c.chute === chute);
            return match ? { chute: match.chute, id: match.id } : null;
          })
          .filter((c): c is { chute: number; id: number } => c !== null);

        const disabledRuleIds = groups
          .map(g => g.group_id)
          .filter((gid): gid is number => typeof gid === 'number')
          .filter((_, i) => i !== index);

        const assignedChutes = groups.flatMap((g, i) => (i === index ? [] : g.chutes));

        return (
          <Form.RuleItem
            key={group.dndId}
            dndId={group.dndId}
            position={index + 1}
            totalCount={groups.length}
            chutes={baseChutes}
            ruleOptions={ruleOptions}
            onDelete={() => handleDeleteRule(group.dndId)}
            onReorder={handleReorder}
            ruleIndex={index}
            onChutesSelected={handleChutesSelected}
            selectedChutes={assignedChutes}
            onGroupUpdate={handleUpdateGroup}
            selectedRuleId={group.group_id?.toString()}
            defaultChutes={defaultChutes}
            disabledRuleIds={disabledRuleIds}
            showChutes={showChutesSection}
            defaultRouteChangeConfig={{
              provider: group.provider || null,
              providerService: group.providerService || null,
            }}
            onRouteChangeUpdate={config => handleUpdateRouteChange(index, config)}
            maxCapacity={group.maxCapacity ?? null}
            onMaxCapacityChange={handleMaxCapacityChange}
          />
        );
      }),
    [
      groups,
      baseChutes,
      ruleOptions,
      showChutesSection,
      handleDeleteRule,
      handleReorder,
      handleChutesSelected,
      handleUpdateGroup,
      handleUpdateRouteChange,
      handleMaxCapacityChange,
    ],
  );

  // ── Data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [template, rules, chutes] = await Promise.all([
          getTemplateDetailUseCase.execute(patternId),
          getAllRules(),
          getAllChutes(),
        ]);

        setPatternData(template);

        const rulesList = rules?.groups || rules || [];
        setRuleOptions(
          (Array.isArray(rulesList) ? rulesList : []).map((g: any) => ({
            value: g.id.toString(),
            label: g.name,
          })),
        );

        const chutesList =
          chutes?.data?.default_chutes || chutes?.default_chutes || chutes || [];
        setBaseChutes(
          (Array.isArray(chutesList) ? chutesList : []).map((item: any) => ({
            id: item.id,
            chute: item.number,
            type: 'available',
          })),
        );
      } catch {
        setRuleOptions([]);
        setBaseChutes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [patternId]);

  useEffect(() => {
    if (patternData && baseChutes.length > 0 && groups.length === 0) {
      setGroups(
        patternData.rules.map((r, i) => ({
          dndId: `existing-${r.groupId ?? i}`,
          group_id: r.groupId,
          chutes: r.chutes,
          maxCapacity: r.maxCapacity ?? null,
          provider: r.provider || null,
          providerService: r.providerService || null,
        })),
      );
    }
    if (patternData?.name && patternName === '') {
      setPatternName(patternData.name);
    }
  }, [patternData, baseChutes]);

  if (loading || !patternData) return <LoaderTransparent />;

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={groups.map(g => g.dndId)}
          strategy={verticalListSortingStrategy}
        >
          <Form
            onAddRule={handleAddRule}
            onEditTemplate={handleEdit}
            templateName={patternName}
            onNameChange={val => setPatternName(val)}
            buttonDisabled={isButtonDisabled}
            currentOperationMode={patternData.operationMode ?? undefined}
          >
            {ruleItems}
          </Form>
        </SortableContext>
      </DndContext>
      {toast.visible && <StatusToast title={toast.title} type={toast.type} />}
    </>
  );
}
